import { useEffect, useState } from "react";
import NextPage from "../components/NextPage";
import { Butterfly } from "../components/Butterflies";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import ShareWink from "../components/ShareWink";
import { getSaltieSelfies, getSaltieWinks, getSaltieWinksPage } from "../lib/api";
import { safeHtml } from "../lib/safeHtml";

// Fallback copy — matches the seed defaults exactly. Used until the fetch
// resolves (or if it fails), so the page never renders empty.
const DEFAULT_PAGE = {
  seo_title: "Saltie Winks\u2122",
  seo_description:
    "The little reminders that God is near. The origin story behind Salty Moo\u2122 \u2014 how God began teaching us in the pasture.",
  hero_title: "Saltie Winks\u2122",
  hero_subtitle: "The little reminders that God is near.",
  quote_line1: "Some of God\u2019s sweetest whispers didn\u2019t come from a church pew\u2026",
  quote_line2: "they came from the pasture.",
  quote_attribution: "\u2014 Stefanie \u00b7 The Salty Moo\u2122",
  what_is_heading: "What Is a Saltie Wink?",
  what_is_body_html:
    "<p>Many believers affectionately call those quiet reminders of God\u2019s presence \u201cGod Winks.\u201d For years, that\u2019s exactly what Stefanie called them, too. As The Salty Moo story began to unfold, she couldn\u2019t help but smile as she realized those same God Winks had found a home in the storybook world as Saltie Winks\u2122\u2014gentle reminders that God is always at work, even in the smallest moments.</p><p>Every Saltie Wink\u2122 is an invitation to pause, smile, and remember that God is often found in the ordinary moments of everyday life.</p>",
  scrapbook_kicker: "From the scrapbook",
  scrapbook_heading: "Latest Saltie Winks",
  closing_blessing_html:
    "<p>We hope you\u2019ll begin noticing your own Saltie Winks\u2122 wherever God leads you.</p>",
  selfies_kicker_with_photos: "A collage",
  selfies_kicker_without_photos_prefix: "A collage, ",
  selfies_kicker_coming_soon: "coming soon",
  selfies_heading: "Saltie Selfies",
  selfies_tagline: "little moments from the pasture \u2014 noticed, remembered, shared.",
  selfies_footer_with_photos: "New selfies are added by the pasture as they roll in.",
  selfies_footer_without_photos:
    "(These squares will fill with real Saltie Selfies as they roll in from the pasture.)",
  card_notice_title: "Notice",
  card_notice_body: "God often whispers through ordinary moments.",
  card_remember_title: "Remember",
  card_remember_body:
    "The butterfly isn\u2019t the Saltie Wink\u2122. The lesson is. The butterfly simply reminds us to notice.",
  card_grow_title: "Grow",
  card_grow_body:
    "Every Saltie Wink\u2122 helps us grow a little closer to the person God created us to be.",
};

export default function SaltieWinks() {
  const [selfies, setSelfies] = useState([]);
  const [winks, setWinks] = useState([]);
  const [page, setPage] = useState(DEFAULT_PAGE);

  useEffect(() => {
    getSaltieSelfies().then(setSelfies).catch(() => {});
    getSaltieWinks()
      .then((rows) => setWinks(Array.isArray(rows) ? rows : []))
      .catch(() => setWinks([]));
    getSaltieWinksPage()
      .then((p) => setPage({ ...DEFAULT_PAGE, ...(p || {}) }))
      .catch(() => {});
  }, []);

  // Cards driven by editable content
  const CARDS = [
    { title: page.card_notice_title, body: page.card_notice_body, tilt: "-1.4deg" },
    { title: page.card_remember_title, body: page.card_remember_body, tilt: "1deg" },
    { title: page.card_grow_title, body: page.card_grow_body, tilt: "-0.8deg" },
  ];

  // Deterministic-ish tile styling so newly uploaded selfies gently rotate
  // and share the same painterly polaroid feel.
  const TILT_PATTERN = [
    { rotate: "-1.4deg", swash: "50% 100%", warm: "top" },
    { rotate: "0.8deg", swash: "100% 60%", warm: "left" },
    { rotate: "-0.6deg", swash: "20% 90%", warm: "top" },
    { rotate: "1.2deg", swash: "70% 80%", warm: "right" },
    { rotate: "-1deg", swash: "40% 100%", warm: "top" },
    { rotate: "0.4deg", swash: "80% 90%", warm: "left" },
    { rotate: "-0.9deg", swash: "60% 80%", warm: "right" },
    { rotate: "1.1deg", swash: "30% 70%", warm: "top" },
  ];
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const asAbsolute = (u) => (u && u.startsWith("http") ? u : `${backendUrl}${u}`);
  const tiles = selfies.length > 0
    ? selfies.map((s, i) => ({
        ...TILT_PATTERN[i % TILT_PATTERN.length],
        selfie: s,
      }))
    : TILT_PATTERN.slice(0, 6).map((t) => ({ ...t, selfie: null }));

  return (
    <div className="parchment min-h-screen relative overflow-hidden" data-testid="saltie-winks-page">
      <SEO
        title={page.seo_title}
        description={page.seo_description}
        path="/saltie-winks"
      />

      <ChapterHero
        path="/saltie-winks"
        image="/assets/heroes/saltie-winks.webp"
        title={page.hero_title}
        subtitle={page.hero_subtitle}
        textColor="#6A2CB2"
        titleClassName="saltie-wink-title"
        overlay={
          <>
            {/* boost the existing sunbeam in the artwork */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 22% 55% at 34% 40%, rgba(255,236,180,0.55) 0%, rgba(255,236,180,0.28) 35%, rgba(255,236,180,0) 70%)",
                mixBlendMode: "screen",
              }}
            />
            {/* soft warm bloom at the beam's origin */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle 18% at 33% 18%, rgba(255,244,206,0.7) 0%, rgba(255,244,206,0.25) 40%, rgba(255,244,206,0) 70%)",
                mixBlendMode: "screen",
              }}
            />
          </>
        }
      />

      <article className="relative max-w-4xl mx-auto px-6 -mt-8 pb-24 z-10">
        {/* warm sunlight halo behind the content */}
        <div
          aria-hidden
          className="absolute -top-24 left-1/2 -translate-x-1/2 w-[80vw] h-[80vw] pointer-events-none sun-rays -z-10"
          style={{
            background:
              "radial-gradient(circle at 50% 20%, rgba(238,232,79,0.22), transparent 55%)",
          }}
        />

        {/* ── Signature Quote ────────────────────────────── */}
        <figure
          data-testid="winks-signature-quote"
          className="relative max-w-3xl mx-auto mt-8 mb-20 text-center px-4"
        >
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <Butterfly />
          </div>

          <blockquote className="font-display italic text-3xl md:text-[2.4rem] leading-[1.35] text-[#4B3A2F]">
            {page.quote_line1}
            <br />
            <span className="text-[#D62839]">{page.quote_line2}</span>
          </blockquote>
          <figcaption className="mt-8 text-[11px] uppercase tracking-[0.4em] text-[#6D5A50]">
            {page.quote_attribution}
          </figcaption>
          <div className="mt-6 flex justify-center">
            <ShareWink text={`${page.quote_line1} ${page.quote_line2}`} />
          </div>
        </figure>

        {/* ── What Is a Saltie Wink? ───────────────────────── */}
        <section
          data-testid="winks-what-is"
          className="max-w-2xl mx-auto text-center my-24"
        >
          <h2 className="font-display text-4xl md:text-5xl text-[#4B3A2F] mb-10">
            {page.what_is_heading}
          </h2>
          <div
            className="font-body text-[#2C2724] text-lg md:text-xl leading-[1.85] space-y-6 text-left prose-winks"
            dangerouslySetInnerHTML={safeHtml(page.what_is_body_html)}
          />
        </section>

        {/* ── The Scrapbook — published winks from the Admin ─── */}
        {winks.length > 0 && (
          <section
            data-testid="winks-scrapbook"
            className="my-24"
          >
            <div className="text-center mb-14">
              <p className="text-xs uppercase tracking-[0.4em] text-[#4B3A2F] mb-3">
                {page.scrapbook_kicker}
              </p>
              <h2 className="font-display text-4xl md:text-5xl text-[#4B3A2F]">
                {page.scrapbook_heading}
                <sup className="text-2xl">™</sup>
              </h2>
              <div className="gold-rule mt-8" />
            </div>

            <div className="space-y-14 max-w-3xl mx-auto">
              {winks.map((w, i) => {
                // Alternate a tiny rotation so the entries feel taped down
                const tilt = i % 2 === 0 ? "-0.5deg" : "0.6deg";
                return (
                  <article
                    key={w.id}
                    data-testid={`wink-entry-${w.id}`}
                    className="relative px-8 md:px-12 py-10 md:py-12 bg-[#FBF7F0] border border-[#E7DFD5]"
                    style={{
                      transform: `rotate(${tilt})`,
                      borderRadius: "4px",
                      boxShadow:
                        "0 1px 0 rgba(255,255,255,0.6) inset, 0 -1px 0 rgba(0,0,0,0.04) inset, 0 22px 30px -18px rgba(0,0,0,0.28), 0 2px 0 rgba(75,58,47,0.10)",
                    }}
                  >
                    {/* washi-tape flourish at the top */}
                    <span
                      aria-hidden
                      className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 opacity-80"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(214,40,57,0.55) 0%, rgba(214,40,57,0.35) 100%)",
                        transform: "translateX(-50%) rotate(-4deg)",
                        boxShadow: "0 4px 6px -3px rgba(0,0,0,0.15)",
                      }}
                    />

                    {/* "when" label — small uppercase */}
                    {w.when && (
                      <p
                        className="text-[11px] uppercase tracking-[0.4em] text-[#A87149] mb-4 text-center"
                        data-testid={`wink-when-${w.id}`}
                      >
                        {w.when}
                      </p>
                    )}

                    {/* the wink itself — preserve author's line breaks */}
                    <div
                      data-testid={`wink-body-${w.id}`}
                      className="font-body text-[#2C2724] text-lg md:text-xl leading-[1.85] whitespace-pre-line"
                    >
                      {w.wink}
                    </div>

                    {/* context / caption in script */}
                    {w.context && (
                      <p
                        className="mt-8 font-script text-2xl md:text-3xl text-[#4B3A2F] text-center"
                        data-testid={`wink-context-${w.id}`}
                      >
                        {w.context}
                      </p>
                    )}

                    {/* share this wink */}
                    <div className="mt-8 flex justify-center">
                      <ShareWink text={w.wink} />
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Closing blessing before Saltie Selfies ───────── */}
        <section
          data-testid="winks-blessing"
          className="max-w-2xl mx-auto text-center my-20"
        >
          <div
            className="font-script text-3xl md:text-4xl text-[#4B3A2F] leading-snug prose-winks-script"
            dangerouslySetInnerHTML={safeHtml(page.closing_blessing_html)}
          />
        </section>

        {/* ── Saltie Selfies — 6-square photo collage placeholder ─── */}
        <section
          data-testid="saltie-selfies"
          className="my-24 text-center"
        >
          <p className="text-xs uppercase tracking-[0.4em] text-[#4B3A2F] mb-3">
            {selfies.length > 0 ? (
              page.selfies_kicker_with_photos
            ) : (
              <>
                {page.selfies_kicker_without_photos_prefix}
                <span className="coming-soon-flash">{page.selfies_kicker_coming_soon}</span>
              </>
            )}
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#4B3A2F] mb-3">
            {page.selfies_heading}
          </h2>
          <p className="font-script text-xl md:text-2xl text-[#4B3A2F] max-w-xl mx-auto mb-10">
            {page.selfies_tagline}
          </p>

          <div
            data-testid="saltie-selfies-grid"
            className="grid grid-cols-2 md:grid-cols-3 gap-5 max-w-3xl mx-auto"
          >
            {tiles.map((tile, i) => (
              <div
                key={tile.selfie?.id || `placeholder-${i}`}
                data-testid={tile.selfie ? `saltie-selfie-${tile.selfie.id}` : `saltie-selfie-placeholder-${i + 1}`}
                className="relative aspect-square w-full flex flex-col items-center justify-center gap-2 text-center px-3 overflow-hidden"
                style={{
                  transform: `rotate(${tile.rotate})`,
                  background:
                    "linear-gradient(160deg, #F9F1E1 0%, #EFE4CE 60%, #E3D3B4 100%)",
                  borderRadius: "6px",
                  border: "1px solid rgba(168,113,73,0.28)",
                  boxShadow:
                    "0 1px 0 rgba(255,255,255,0.6) inset, 0 -1px 0 rgba(0,0,0,0.04) inset, 0 18px 26px -18px rgba(75,58,47,0.4), 0 2px 0 rgba(75,58,47,0.10)",
                }}
              >
                {tile.selfie ? (
                  <img
                    src={asAbsolute(tile.selfie.url)}
                    alt={tile.selfie.caption || "Saltie Selfie"}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    draggable="false"
                  />
                ) : (
                  <>
                    {/* soft green pasture wash */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background: `radial-gradient(ellipse at ${tile.swash}, rgba(126,146,104,0.25), transparent 65%)`,
                      }}
                    />
                    {/* honey light corner */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background: `radial-gradient(ellipse at ${tile.warm === "left" ? "0% 0%" : tile.warm === "right" ? "100% 0%" : "50% 0%"}, rgba(238,232,79,0.28), transparent 55%)`,
                      }}
                    />
                    <div className="relative text-4xl md:text-5xl select-none" aria-hidden>
                      📷
                    </div>
                    <p className="relative text-[9px] md:text-[10px] uppercase tracking-[0.35em] text-[#6D5A50]">
                      Saltie Selfie #{i + 1}
                    </p>
                  </>
                )}
                {tile.selfie?.caption && (
                  <p className="absolute bottom-0 left-0 right-0 bg-[#4B3A2F]/70 text-white text-[10px] md:text-xs px-2 py-1 font-body italic">
                    {tile.selfie.caption}
                  </p>
                )}
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-xl mx-auto text-sm md:text-base italic text-[#6D5A50] leading-relaxed">
            {selfies.length > 0
              ? page.selfies_footer_with_photos
              : page.selfies_footer_without_photos}
          </p>
        </section>

        {/* ── Three illustrated cards — Notice · Remember · Grow ── */}
        <section
          data-testid="winks-three-cards"
          className="grid md:grid-cols-3 gap-8 my-20"
        >
          {CARDS.map((c) => (
            <div
              key={c.title}
              data-testid={`wink-card-${(c.title || "").toLowerCase()}`}
              className="relative bg-[#F9F6F0] border border-[#E7DFD5] p-8 text-center"
              style={{
                transform: `rotate(${c.tilt})`,
                borderRadius: "4px",
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.6) inset, 0 -1px 0 rgba(0,0,0,0.04) inset, 0 22px 30px -18px rgba(0,0,0,0.35), 0 2px 0 rgba(75,58,47,0.14)",
              }}
            >
              <div className="flex justify-center mb-4 h-16">
                <img
                  src="/assets/sm-pastel-symbol.png"
                  alt=""
                  aria-hidden
                  className="h-full w-auto select-none"
                  draggable="false"
                  style={{ filter: "drop-shadow(0 3px 6px rgba(75,58,47,0.18))" }}
                />
              </div>
              <h3 className="font-display text-2xl text-[#4B3A2F] mb-3">
                {c.title}
              </h3>
              <div
                aria-hidden
                className="mx-auto w-10 h-px bg-[#A87149]/50 mb-4"
              />
              <p className="font-body text-[15px] leading-relaxed text-[#2C2724]/85">
                {c.body}
              </p>
            </div>
          ))}
        </section>
      </article>

      <NextPage to="/our-cow-munity" label="Our Cow-munity" />
    </div>
  );
}
